import { NgIf } from '@angular/common';
import { Component, Input, ViewChild, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  IonButton,
  IonButtons,
  IonCol,
  IonContent,
  IonFooter,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonPopover,
  IonRow,
  IonTitle,
  IonToolbar,
} from '@ionic/angular/standalone';
import { TranslatePipe } from '@ngx-translate/core';
import { ClassificationCode, IPaymentDetailsDto } from 'src/app/contracts';
import { InternalError } from 'src/app/core/contracts';
import { HourlyData } from 'src/app/data/hourly.data';
import { MonthlyData } from 'src/app/data/monthly.data';
import { PaymentService } from 'src/app/services/payment.service';
import { BaseModal } from 'src/app/shared/components/base-modal/base.modal';

@Component({
  templateUrl: 'emkan.modal.html',
  styleUrls: ['emkan.modal.scss'],
  imports: [
    FormsModule,
    NgIf,
    TranslatePipe,
    IonPopover,
    IonContent,
    IonToolbar,
    IonHeader,
    IonButtons,
    IonFooter,
    IonTitle,
    IonRow,
    IonCol,
    IonItem,
    IonIcon,
    IonLabel,
    IonButton,
    IonInput,
  ],
})
export class EmkanModalComponent extends BaseModal {
  public override title: string = 'Emkan';
  private payment = inject(PaymentService);
  private hourlyData = inject(HourlyData);
  private monthlyData = inject(MonthlyData);

  @ViewChild(IonContent, { static: false }) content!: IonContent;
  @Input({ required: true }) requestId!: number;
  @Input({ required: true }) classificationCode!: ClassificationCode;
  @Input({ required: true }) usingWallet!: boolean;
  voucher = '';
  applicationId = '';
  details?: IPaymentDetailsDto;
  voucherAmount: number | null = null;
  showOTPPopover = false;
  otp: string | null = null;
  override ngOnInit(): void {
    super.ngOnInit();
    if (PaymentService.isMonthly(this.classificationCode)) {
      this.monthlyData.getPaymentDetails(this.requestId, { useWallet: this.usingWallet }).subscribe(details => {
        this.details = details;
      });
    } else if (PaymentService.isHourly(this.classificationCode)) {
      this.hourlyData.getPaymentDetails(this.requestId, { useWallet: this.usingWallet }).subscribe(details => {
        this.details = details;
      });
    } else {
      this.close();
      throw new InternalError(`Payment classification not supported: '${this.classificationCode}'`);
    }
  }
  getVoucherAmount() {
    if (!this.voucher) {
      this.ui.showError(this.translation.Payment.PleaseEnterVoucherNumber);
      return;
    }
    this.voucherAmount = null;
    this.payment.getEmkanVoucherAmount(this.voucher, this.applicationId).subscribe(amount => {
      this.voucherAmount = amount;
      this.scrollToElement('emkan-footer');
    });
  }
  scrollToElement(id: string) {
    const element = document.getElementById(id);
    if (element) {
      this.content.scrollToPoint(0, element.offsetTop, 1200);
    }
  }
  copyPrice() {
    if (navigator.clipboard) {
      navigator.clipboard
        .writeText(this.details?.amountToPay.toString() || '')
        .then(() => {
          this.ui.showToast(this.translation.Payment.PriceCopiedToClipboard);
        })
        .catch(() => {
          this.ui.showToast(this.translation.Payment.FailedToCopyPriceToClipboard);
        });
    } else {
      this.ui.showError(this.translation.Errors.SorryNoClipboardSupport);
    }
  }
  async useVoucher() {
    if (!this.voucher) {
      this.ui.showError(this.translation.Payment.PleaseEnterVoucherNumber);
      return;
    }
    if (this.notEnoughBalanceToActivate()) {
      const agree = await this.ui.showConfirm(this.translation.Payment.VoucherAmountIsNotEnoughtToActivateRequest);
      if (!agree) {
        return;
      }
    }
    if (this.hasExcessAmount()) {
      const dialog = await this.ui.showMessage(this.translation.Payment.ExcessAmountWillBeAddedToYourWallet);
      await dialog.onDidDismiss();
    }
    this.payment.requestEmkanVoucherOTP(this.voucher, this.applicationId, `${this.classificationCode}${this.requestId}`).subscribe(() => {
      this.otp = null;
      this.showOTPPopover = true;
    });
  }
  notEnoughBalanceToActivate() {
    if (!this.voucherAmount || !this.details) {
      return true;
    }
    if (this.details.usingWallet) {
      return this.voucherAmount < this.details.amountToPay; // amountToPay already has wallet balance deducted because usingWallet
    } else {
      // voucher amount will be added to wallet then the request will get activated with the total amount in the wallet
      return this.voucherAmount + this.details.walletBalance < this.details.amountToPay;
    }
  }
  hasExcessAmount() {
    if (!this.voucherAmount || !this.details) {
      return false;
    }
    return this.voucherAmount > this.details.amountToPay;
  }
  redeemVoucher(popover: IonPopover) {
    if (!this.voucher) {
      this.ui.showError(this.translation.Payment.PleaseEnterVoucherNumber);
      return;
    }
    if (!this.otp) {
      this.ui.showError(this.translation.Errors.PleaseEnterOTP);
      return;
    }
    this.payment.redeemEmkanVoucher(this.voucher, this.applicationId, this.otp, `${this.classificationCode}${this.requestId}`).subscribe(() => {
      popover.dismiss();
      const isActivated = !this.notEnoughBalanceToActivate();
      this.close(isActivated);
    });
  }
}
